import CustomChartTooltip from 'common/components/CustomChartTooltip'
import BaseOptionChart from 'common/components/charts/BaseOptionChart'
import { createArrayOfNumber } from 'common/utils/array'
import { dynamicTickAmount, integerFormatter } from 'common/utils/chart'
import { fNumber } from 'common/utils/formatNumber'
import merge from 'lodash/merge'
import { useGetFoodStandard } from 'modules/comparisonPond/api/queries'
import { useGetFoodLineChart, useGetPartials } from 'modules/food/api/queries'
import { useGetXTitleLineChart, useSpeciesName } from 'modules/pond/api/selector'
import { selectedCycleIdState } from 'modules/pond/stores/atom'
import { useMemo } from 'react'
import ReactApexChart from 'react-apexcharts'
import { renderToString } from 'react-dom/server'
import { useTranslation } from 'react-i18next'
import { useRecoilValue } from 'recoil'

import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import { useShrimpEventAnnotations } from 'hooks/useShrimpEventAnnotations'

const FoodLineChart = () => {
  const { t } = useTranslation('food')
  const { data, isFetching } = useGetFoodLineChart()
  const theme = useTheme()
  const xTitle = useGetXTitleLineChart()
  const { data: partials } = useGetPartials()
  const speciesName = useSpeciesName()
  const eventAnnotations = useShrimpEventAnnotations(partials)
  const cycleId = useRecoilValue(selectedCycleIdState)
  const { data: foodStandardData, isLoading: isLoadingFoodStandard } = useGetFoodStandard(
    cycleId ? [cycleId] : []
  )

  const ages = useMemo(() => {
    const uniqueAges = new Set(data?.map((point) => Number(point.shrimpAge)))
    return Array.from(uniqueAges).sort((a, b) => a - b)
  }, [data])

  const filteredStandards = useMemo(() => {
    return foodStandardData?.filter((standard) => ages.includes(Number(standard.age)))
  }, [foodStandardData, ages])

  const getSeries = () => {
    const result: Record<
      'totalFood' | 'totalRemainingFood' | 'accumulatedFood' | 'totalNaturalFeed',
      number[][]
    > = {
      totalFood: [],
      totalRemainingFood: [],
      accumulatedFood: [],
      totalNaturalFeed: [],
    }
    if (!data) return result

    data.forEach((el) => {
      result.totalFood.push([el.shrimpAge, el.totalFood])
      result.totalRemainingFood.push([el.shrimpAge, el.totalRemainingFood])
      result.accumulatedFood.push([el.shrimpAge, el.accumulatedFood])
      result.totalNaturalFeed.push([el.shrimpAge, el.totalNaturalFeed ?? []])
    })

    return result
  }

  const getChartData = () => {
    const series = [{ name: t('total.food.consume'), data: getSeries().totalFood }]

    // Add standard line series
    if (filteredStandards?.length) {
      const standardSeries = filteredStandards.map((item) => [Number(item.age), item.total_foods])
      series.push({
        name: t('standard'),
        data: standardSeries,
      })
    }

    return series
  }

  const baseYaxisOption = {
    title: {
      text: t('food.amount.kg'),
    },
    labels: {
      formatter: (value: string | number) => {
        if (typeof value !== 'undefined') return fNumber(value, { disabledDecimal: true })
        return value
      },
    },
  }

  const chartOptions = merge(BaseOptionChart({ showPartialAnnotation: true }), {
    xaxis: {
      type: 'numeric',
      title: {
        text: xTitle,
      },
      tickAmount: dynamicTickAmount(data?.length || 0),
      labels: {
        formatter: integerFormatter,
      },
    },
    yaxis: baseYaxisOption,
    stroke: {
      width: 2,
      dashArray: getChartData().map((s) => (s.name === t('standard') ? 5 : 0)),
    },
    colors: getChartData().map((s) => (s.name === t('standard') ? '#D3D3D3' : undefined)),
    tooltip: {
      custom: ({
        dataPointIndex,
        series,
        seriesIndex,
        w,
      }: {
        dataPointIndex: number
        series: number[][]
        seriesIndex: number
        w: { globals: { seriesX: number[][] } }
      }) => {
        const standardSeries = filteredStandards?.map((s) => [Number(s.age), s.total_foods]) || []

        const tooltipData = [
          {
            title: `${t('total.food.consume')}:`,
            value: `${fNumber(series[seriesIndex][dataPointIndex])} ${t('kilo')}`,
          },
        ]

        if (standardSeries.length > 0) {
          tooltipData.push({
            title: `${t('standard')}:`,
            value: `${fNumber(
              standardSeries.find(
                (s) => s[0] === w.globals.seriesX[seriesIndex][dataPointIndex]
              )?.[1] ?? 0
            )} ${t('kilo')}`,
          })
        }
        return renderToString(
          <CustomChartTooltip
            title={`${t('shrimp.age', { speciesName })}: ${
              w.globals.seriesX[seriesIndex][dataPointIndex]
            } ${t('day')}`}
            data={tooltipData}
          />
        )
      },
    },
    annotations: {
      xaxis: eventAnnotations,
    },
    responsive: [
      {
        breakpoint: theme.breakpoints.values.sm,
        options: {
          legend: {
            position: 'bottom',
          },
          yaxis: {
            title: {
              text: undefined,
            },
            labels: {
              ...baseYaxisOption.labels,
              offsetX: -10,
            },
          },
          xaxis: {
            title: {
              offsetY: 30,
            },
          },
        },
      },
    ],
  })

  if (isFetching)
    return (
      <Stack spacing={3} p={2}>
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          {createArrayOfNumber(3).map((i) => (
            <Skeleton width={60} key={i} />
          ))}
        </Stack>
        {createArrayOfNumber(6).map((i) => (
          <Stack direction="row" key={i} spacing={1}>
            <Skeleton width={40} />
            <Skeleton width="100%" />
          </Stack>
        ))}
        <Stack direction="row" spacing={2}>
          {createArrayOfNumber(6).map((i) => (
            <Skeleton key={i} width="100%" />
          ))}
        </Stack>
      </Stack>
    )

  return <ReactApexChart type="line" series={getChartData()} options={chartOptions} height={420} />
}

export default FoodLineChart
